import React, {
    HTMLAttributes,
    ButtonHTMLAttributes,
    useEffect,
    useMemo,
    useState,
} from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import { BRANDING_UPDATED_EVENT, loadAffiliateBrandingFromStorage } from 'helpers/co2helpers/affiliate_branding_helper';
import { adjustColor, hexToRgba, isColorDark } from 'utils/color';

type ButtonVariant = 'primary' | 'secondary' | 'alternate1' | 'alternate2' | 'danger' | 'warning' | 'add' | 'search' | 'delete' | 'voice' | 'ai_format' | 'upload' | 'edit' | 'other' | 'calendar' | 'current_location' | 'swap' | 'date_single' | 'date_range' | 'pdf' | 'excel' | 'image' | 'custom';
type Appearance = 'light' | 'dark';
type CircleButtonSize = 'small' | 'medium' | 'medium-lg' | 'large';

export const DARK_ALPHA = 0.25;
const HOVER_SHIFT = 0.2;

type PaletteColor = {
    background: string;
    text: string;
    darkBackground?: string;
    darkText?: string;
    hover?: string;
    darkHover?: string;
    border?: string;
    darkBorder?: string;
    icon?: string;
    darkIcon?: string;
};

export type Co2CircleButtonProps = {
    variant?: ButtonVariant;
    iconVariant?: ButtonVariant;
    iconClass?: string;
    iconColor?: string;
    loading?: boolean;
    themeOverride?: Appearance;
    paletteOverride?: Partial<Record<Exclude<ButtonVariant, 'custom'>, PaletteColor>>;
    useBrandingPalette?: boolean;
    size?: CircleButtonSize;
    customColor?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export type Co2CircleButtonGroupProps = HTMLAttributes<HTMLDivElement>;

const DEFAULT_PALETTE: Record<Exclude<ButtonVariant, 'custom'>, PaletteColor> = {
    primary: { background: '#0AB39C', darkBackground: '#0AB39C', hover: '#099885', darkHover: '#177C70', border: '#31D4BF', darkBorder: '#189C8D', text: '#ffffff', darkText: '#ffffff', icon: '#ffffff', darkIcon: '#ffffff' },
    secondary: { background: '#F5F5F5', darkBackground: '#1C4992', hover: '#e8e8e8', darkHover: '#173d7b', border: '#EDEAEA', darkBorder: '#1C4D9B', text: '#000000', darkText: '#ffffff', icon: '#000000', darkIcon: '#ffffff' },
    alternate1: { background: '#50c3e6', darkBackground: '#50C3E6', hover: '#35b8df', darkHover: '#1b8fac', border: '#71CAE6', darkBorder: '#197C95', text: '#FAFAFA', darkText: '#ffffff', icon: '#FFFFFF', darkIcon: '#ffffff' },
    alternate2: { background: '#89A3E0', darkBackground: '#89A3E0', hover: '#7B9BE6', darkHover: '#7B9BE6', border: '#9BB2E8', darkBorder: '#9BB2E8', text: '#FCFDFE', darkText: '#ffffff', icon: '#FFFFFF', darkIcon: '#ffffff' },
    danger: { background: '#dc3545', text: '#ffffff' },
    warning: { background: '#f7b84b', text: '#ffffff' },
    add: { background: '#DFF0ED', darkBackground: '#25D6C2', hover: '#AFEDE5', darkHover: '#146E63', border: '#DFF0ED', darkBorder: '#19998A', text: '#11A392', darkText: '#ffffff', icon: '#11A392', darkIcon: '#ffffff' },
    search: { background: '#ECEEF7', darkBackground: '#1C4992', hover: '#D3DBFB', darkHover: '#164590', border: '#ECEEF7', darkBorder: '#24539E', text: '#6272C9', darkText: '#ffffff', icon: '#6272C9', darkIcon: '#ffffff' },
    delete: { background: '#F2E7E7', darkBackground: '#b02a37', hover: '#F0D5D5', darkHover: '#93232e', border: '#F2E7E7', darkBorder: '#b02a37', text: '#EF6E6E', darkText: '#ffffff', icon: '#EF6E6E', darkIcon: '#ffffff' },
    voice: { background: '#E4E9F4', darkBackground: '#3f5b96', hover: '#C4D4F5', darkHover: '#334a7a', border: '#E4E9F4', darkBorder: '#3f5b96', text: '#6C87C7', darkText: '#ffffff', icon: '#6C87C7', darkIcon: '#ffffff' },
    ai_format: { background: '#EAE1F0', darkBackground: '#20a8ca', hover: '#CDA7E6', darkHover: '#1b8fac', border: '#EAE1F0', darkBorder: '#20a8ca', text: '#4F1675', darkText: '#ffffff', icon: '#4F1675', darkIcon: '#ffffff' },
    upload: { background: '#E8EAF4', darkBackground: '#4F63C6', hover: '#CFD5F2', darkHover: '#2D41A4', border: '#E8EAF4', darkBorder: '#4F63C6', text: '#4D66E4', darkText: '#E3E4E7', icon: '#4D66E4', darkIcon: '#E3E4E7' },
    edit: { background: '#E5F5F3', darkBackground: '#3f5b96', hover: '#CFF4EE', darkHover: '#334a7a', border: '#E5F5F3', darkBorder: '#3f5b96', text: '#11A392', darkText: '#ffffff', icon: '#11A392', darkIcon: '#ffffff' },
    other: { background: '#F7EEEE', darkBackground: '#1C4992', hover: '#F3D6D6', darkHover: '#173d7b', border: '#F7EEEE', darkBorder: '#1C4992', text: '#7C2A11', darkText: '#ffffff', icon: '#7C2A11', darkIcon: '#ffffff' },
    calendar: { background: '#ECEEF7', darkBackground: '#1C4992', hover: '#D3DBFB', darkHover: '#164590', border: '#ECEEF7', darkBorder: '#24539E', text: '#6272C9', darkText: '#ffffff', icon: '#6272C9', darkIcon: '#ffffff' },
    current_location: { background: '#E5F5F3', darkBackground: '#3f5b96', hover: '#CFF4EE', darkHover: '#334a7a', border: '#E5F5F3', darkBorder: '#3f5b96', text: '#11A392', darkText: '#ffffff', icon: '#11A392', darkIcon: '#ffffff' },
    swap: { background: '#50c3e6', darkBackground: '#50C3E6', hover: '#35b8df', darkHover: '#1b8fac', border: '#71CAE6', darkBorder: '#197C95', text: '#FAFAFA', darkText: '#ffffff', icon: '#FFFFFF', darkIcon: '#ffffff' },
    date_single: { background: '#ECEEF7', darkBackground: '#1C4992', hover: '#D3DBFB', darkHover: '#164590', border: '#ECEEF7', darkBorder: '#24539E', text: '#6272C9', darkText: '#ffffff', icon: '#6272C9', darkIcon: '#ffffff' },
    date_range: { background: '#ECEEF7', darkBackground: '#1C4992', hover: '#D3DBFB', darkHover: '#164590', border: '#ECEEF7', darkBorder: '#24539E', text: '#6272C9', darkText: '#ffffff', icon: '#6272C9', darkIcon: '#ffffff' },
    pdf: { background: '#FCE7E7', darkBackground: '#7a1f1f', hover: '#F9D2D2', darkHover: '#601818', border: '#FCE7E7', darkBorder: '#7a1f1f', text: '#FF0000', darkText: '#ffffff', icon: '#FF0000', darkIcon: '#ffffff' },
    excel: { background: '#E1F0E7', darkBackground: '#1D6F42', hover: '#C7E7D4', darkHover: '#175a35', border: '#E1F0E7', darkBorder: '#1D6F42', text: '#1D6F42', darkText: '#ffffff', icon: '#1D6F42', darkIcon: '#ffffff' },
    image: { background: '#EDE4F5', darkBackground: '#6f42c1', hover: '#DECBEE', darkHover: '#59349a', border: '#EDE4F5', darkBorder: '#6f42c1', text: '#6f42c1', darkText: '#ffffff', icon: '#6f42c1', darkIcon: '#ffffff' },
};

const DEFAULT_ICON_CLASSES: Partial<Record<Exclude<ButtonVariant, 'custom'>, string>> = {
    add: 'fa-classic fa-solid fa-plus',
    search: 'fa-classic fa-solid fa-magnifying-glass',
    delete: 'fa-classic fa-solid fa-trash',
    voice: 'fa-classic fa-solid fa-microphone',
    ai_format: 'fa-classic fa-light fa-microchip-ai',
    upload: 'fa-classic fa-regular fa-upload',
    edit: 'fa fa-solid fa-pen-to-square',
    calendar: 'fa fa-solid fa-calendar',
    current_location: 'fa-classic fa-solid fa-location-crosshairs',
    swap: 'fa-classic fa-solid fa-arrow-right-arrow-left',
    date_single: 'fa fa-solid fa-calendar-day',
    date_range: 'fa fa-solid fa-calendar-range',
    pdf: 'fa-light fa-file-pdf',
    excel: 'fa-light fa-file-excel',
    image: 'fa-light fa-image',
};

const SIZE_MAP: Record<CircleButtonSize, { button: number; icon: number }> = {
    small: { button: 22, icon: 12 },
    medium: { button: 28, icon: 12 },
    'medium-lg': { button: 28, icon: 14 },
    large: { button: 34, icon: 20 },
};

const getThemeAppearance = (): Appearance => {
    const attr =
        typeof document !== 'undefined'
            ? document.documentElement.getAttribute('data-bs-theme')
            : undefined;

    if (attr === 'dark') return 'dark';
    if (attr === 'light') return 'light';

    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
    }

    return 'light';
};

export const useThemeAppearance = (override?: Appearance) => {
    const [appearance, setAppearance] = useState<Appearance>(() =>
        override ?? getThemeAppearance()
    );

    useEffect(() => {
        if (override) {
            setAppearance(override);
            return;
        }

        const update = () => setAppearance(getThemeAppearance());

        let mq: MediaQueryList | undefined;
        if (typeof window !== 'undefined' && window.matchMedia) {
            mq = window.matchMedia('(prefers-color-scheme: dark)');
            try {
                mq.addEventListener?.('change', update);
            } catch {
                mq?.addListener?.(update);
            }
        }

        let observer: MutationObserver | undefined;
        if (typeof document !== 'undefined') {
            observer = new MutationObserver(update);
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-bs-theme'],
            });
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('app:themechange', update);
        }

        return () => {
            if (mq) {
                try {
                    mq.removeEventListener?.('change', update);
                } catch {
                    mq?.removeListener?.(update);
                }
            }
            observer?.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('app:themechange', update);
            }
        };
    }, [override]);

    return appearance;
};

const Co2CircleButton = ({
    variant,
    iconVariant,
    iconClass,
    iconColor,
    loading,
    disabled,
    style,
    onClick,
    themeOverride,
    paletteOverride,
    useBrandingPalette = true,
    type: buttonType,
    className,
    size = 'small',
    customColor,
    ...rest
}: Co2CircleButtonProps) => {
    const brandingButtonsFromRedux = useSelector(
        (state: any) => state?.Co2Profile?.userAffiliate?.branding_buttons
    );
    const [brandingButtonsFromStorage, setBrandingButtonsFromStorage] = useState(() => {
        const stored = loadAffiliateBrandingFromStorage();
        return stored?.branding_buttons ?? null;
    });

    const brandingButtons = useMemo(() => {
        if (brandingButtonsFromRedux) return brandingButtonsFromRedux;
        return brandingButtonsFromStorage;
    }, [brandingButtonsFromRedux, brandingButtonsFromStorage]);

    useEffect(() => {
        const handleBrandingUpdate = () => {
            const stored = loadAffiliateBrandingFromStorage();
            setBrandingButtonsFromStorage(stored?.branding_buttons ?? null);
        };

        window.addEventListener(BRANDING_UPDATED_EVENT, handleBrandingUpdate);
        return () => window.removeEventListener(BRANDING_UPDATED_EVENT, handleBrandingUpdate);
    }, []);

    const fromSettings = useMemo(() => {
        if (!useBrandingPalette) return null;
        if (!brandingButtons) return null;

        try {
            const raw =
                typeof brandingButtons === 'string'
                    ? JSON.parse(brandingButtons)
                    : brandingButtons;
            const toPalette = (
                variantKey: Exclude<ButtonVariant, 'custom'>,
                bgKey: string,
                darkKey: string,
                hoverKey: string,
                darkHoverKey: string,
                borderKey: string,
                darkBorderKey: string,
                iconKey: string,
                darkIconKey: string
            ): PaletteColor | null => {
                const bg = raw?.[bgKey];
                if (!bg) return null;
                const dark = raw?.[darkKey];
                const hover = raw?.[hoverKey];
                const darkHover = raw?.[darkHoverKey];
                const border = raw?.[borderKey];
                const darkBorder = raw?.[darkBorderKey];
                const icon = raw?.[iconKey];
                const darkIcon = raw?.[darkIconKey];
                const fallbackText = DEFAULT_PALETTE[variantKey]?.text ?? '#ffffff';
                const iconColor = icon ? String(icon) : fallbackText;
                const darkIconColor = darkIcon ? String(darkIcon) : iconColor;
                return {
                    background: String(bg),
                    text: iconColor,
                    ...(dark ? { darkBackground: String(dark) } : {}),
                    ...(hover ? { hover: String(hover) } : {}),
                    ...(darkHover ? { darkHover: String(darkHover) } : {}),
                    ...(border ? { border: String(border) } : {}),
                    ...(darkBorder ? { darkBorder: String(darkBorder) } : {}),
                    icon: iconColor,
                    darkIcon: darkIconColor,
                    darkText: darkIconColor,
                };
            };

            const circleVariants = [
                'add',
                'search',
                'delete',
                'voice',
                'ai_format',
                'upload',
                'edit',
                'other',
                'calendar',
                'current_location',
                'swap',
                'date_single',
                'date_range',
                'pdf',
                'excel',
                'image',
            ] as const;
            const circlePalette = circleVariants.reduce((acc, key) => {
                const palette = toPalette(
                    key,
                    `circle_${key}_bg`,
                    `circle_${key}_bg_dark`,
                    `circle_${key}_hover`,
                    `circle_${key}_hover_dark`,
                    `circle_${key}_border`,
                    `circle_${key}_border_dark`,
                    `circle_${key}_icon`,
                    `circle_${key}_icon_dark`
                );
                if (palette) acc[key] = palette;
                return acc;
            }, {} as Partial<Record<Exclude<ButtonVariant, 'custom'>, PaletteColor>>);

            const toStandardPalette = (
                variantKey: Extract<ButtonVariant, 'primary' | 'secondary' | 'alternate1' | 'alternate2'>
            ): PaletteColor | null => {
                const bg = raw?.[`${variantKey}_bg`];
                const text = raw?.[`${variantKey}_text`];
                if (!bg || !text) return null;
                const dark = raw?.[`${variantKey}_bg_dark`];
                const hover = raw?.[`${variantKey}_hover`];
                const darkHover = raw?.[`${variantKey}_hover_dark`];
                const border = raw?.[`${variantKey}_border`];
                const darkBorder = raw?.[`${variantKey}_border_dark`];
                const icon = raw?.[`${variantKey}_icon`];
                const darkIcon = raw?.[`${variantKey}_icon_dark`];
                const darkText = raw?.[`${variantKey}_text_dark`];
                return {
                    background: String(bg),
                    text: String(icon ?? text),
                    darkText: String(darkIcon ?? darkText ?? icon ?? text),
                    ...(dark ? { darkBackground: String(dark) } : {}),
                    ...(hover ? { hover: String(hover) } : {}),
                    ...(darkHover ? { darkHover: String(darkHover) } : {}),
                    ...(border ? { border: String(border) } : {}),
                    ...(darkBorder ? { darkBorder: String(darkBorder) } : {}),
                    ...(icon ? { icon: String(icon) } : {}),
                    ...(darkIcon ? { darkIcon: String(darkIcon) } : {}),
                };
            };

            const primary = toStandardPalette('primary');
            const secondary = toStandardPalette('secondary');
            const alternate1 = toStandardPalette('alternate1');
            const alternate2 = toStandardPalette('alternate2');

            return {
                ...(primary ? { primary } : {}),
                ...(secondary ? { secondary } : {}),
                ...(alternate1 ? { alternate1 } : {}),
                ...(alternate2 ? { alternate2 } : {}),
                danger: DEFAULT_PALETTE.danger,
                warning: DEFAULT_PALETTE.warning,
                ...circlePalette,
            } as Partial<Record<Exclude<ButtonVariant, 'custom'>, PaletteColor>>;
        } catch {
            return null;
        }
    }, [brandingButtons, useBrandingPalette]);

    const resolvedAppearance = useThemeAppearance(themeOverride);
    const darkMode = resolvedAppearance === 'dark';
    const resolvedVariant: ButtonVariant = variant ?? iconVariant ?? 'alternate1';

    const brandingIconClass = useMemo(() => {
        if (!useBrandingPalette) return null;
        if (!brandingButtons) return null;
        if (resolvedVariant === 'custom' || resolvedVariant === 'other') return null;
        if (!DEFAULT_ICON_CLASSES[resolvedVariant as Exclude<ButtonVariant, 'custom'>]) return null;

        try {
            const raw =
                typeof brandingButtons === 'string'
                    ? JSON.parse(brandingButtons)
                    : brandingButtons;
            const value = raw?.[`circle_${resolvedVariant}_icon_class`];
            return value ? String(value) : null;
        } catch {
            return null;
        }
    }, [brandingButtons, resolvedVariant, useBrandingPalette]);

    const palette = useMemo(
        () => ({
            ...DEFAULT_PALETTE,
            ...(fromSettings || {}),
            ...(paletteOverride || {}),
        }),
        [fromSettings, paletteOverride]
    );

    const customPalette = useMemo<PaletteColor | null>(() => {
        if (resolvedVariant !== 'custom' || !customColor) return null;
        return {
            background: customColor,
            darkBackground: customColor,
            text: isColorDark(customColor) ? '#ffffff' : '#000000',
            darkText: isColorDark(customColor) ? '#ffffff' : '#000000',
        };
    }, [resolvedVariant, customColor]);

    const resolvedPalette = resolvedVariant === 'custom'
        ? customPalette
        : palette[resolvedVariant];

    const fallbackPalette = palette.alternate1;
    const activePalette = resolvedPalette ?? fallbackPalette;

    const baseBackgroundLight = activePalette.background;
    const baseBackgroundDark = activePalette.darkBackground ?? activePalette.background;
    const baseText = activePalette.text;
    const baseTextDark = activePalette.darkText ?? baseText;
    const baseHoverLight = activePalette.hover;
    const baseHoverDark = activePalette.darkHover ?? baseHoverLight;
    const baseBorderLight = activePalette.border;
    const baseBorderDark = activePalette.darkBorder ?? baseBorderLight;
    const baseIconLight = activePalette.icon ?? baseText;
    const baseIconDark = activePalette.darkIcon ?? baseTextDark;

    const hoverBackground = useMemo(() => {
        const explicitHover = darkMode ? baseHoverDark : baseHoverLight;
        if (explicitHover) return explicitHover;
        const base = darkMode ? baseBackgroundDark : baseBackgroundLight;
        const shouldLighten = isColorDark(base);
        const adjusted = adjustColor(base, HOVER_SHIFT, shouldLighten);
        return darkMode ? hexToRgba(adjusted, DARK_ALPHA) : adjusted;
    }, [baseBackgroundDark, baseBackgroundLight, baseHoverDark, baseHoverLight, darkMode]);

    const [hovered, setHovered] = useState(false);
    const background = hovered ? hoverBackground : (darkMode ? hexToRgba(baseBackgroundDark, DARK_ALPHA) : baseBackgroundLight);
    const color = darkMode ? baseIconDark : baseIconLight;
    const borderColor = darkMode ? baseBorderDark : baseBorderLight;
    const dimensions = SIZE_MAP[size];
    const resolvedIconClass = resolvedVariant === 'other'
        ? iconClass
        : brandingIconClass ?? iconClass ?? DEFAULT_ICON_CLASSES[resolvedVariant as Exclude<ButtonVariant, 'custom'>];

    return (
        <button
            type={buttonType ?? 'button'}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            disabled={disabled || loading}
            className={classnames('co2-circle-button', 'rounded-circle', className)}
            style={{
                background,
                color,
                border: borderColor ? `1px solid ${borderColor}` : 'none',
                outline: 'none',
                boxShadow: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: dimensions.button,
                height: dimensions.button,
                minWidth: dimensions.button,
                minHeight: dimensions.button,
                padding: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'background-color 150ms ease, color 150ms ease',
                ...style,
            }}
            {...rest}
        >
            {loading ? (
                <Spinner size="sm" color={darkMode ? 'light' : undefined} />
            ) : (
                <i
                    className={resolvedIconClass}
                    style={{ color: iconColor ?? 'inherit', fontSize: dimensions.icon }}
                    aria-hidden
                />
            )}
        </button>
    );
};

export default Co2CircleButton;

export const Co2CircleButtonGroup = ({
    className,
    ...rest
}: Co2CircleButtonGroupProps) => (
    <div
        className={classnames('co2-circle-button-group', className)}
        style={{ marginBottom: 1 }}
        {...rest}
    />
);
